import { useIsMsalAuth, Flex } from '@equifinance/shared';
import { useState } from 'react';
import styled from 'styled-components';
import { stageOnOrAfter } from '../../../contexts/helpers';
import { useAccountContext } from '../../../contexts';
import { Stages } from '../../../types/Stages';
import SidebarFooterItem from './SidebarFooterItem';

const MenuTitleCollapsibleIcon = styled.i` ${({ theme }) => `
  font-size: ${theme.fontSize[3]};
  margin-left: ${theme.padding[8]};
  cursor: pointer;
`}`;

const SidebarFooterMenuItem = styled(Flex)` ${({ theme }) => `
  background: ${theme.colours.tertiary[5]} !important;
  align-items:center;
  justify-content: space-between;
  padding: ${theme.padding[5]};
  padding-top: ${theme.padding[3]};
  padding-bottom: ${theme.padding[3]};
  font-family: Montserrat;
  font-weight: ${theme.fontWeight.bold};
  font-size: ${theme.fontSize[2]};
`}`;

export default function ProductSidebarSummary() {
  const { account, summary, creditSearchCompleted } = useAccountContext();
  const isMsalAuth = useIsMsalAuth();

  const { stage } = account ?? {};

  const [open, setOpen] = useState<boolean>(true);

  const onClickMenuTitle = () => {
    setOpen((prev) => !prev);
  };

  const {
    netLoanAmount, addedFeesAmount, grossLoanAmount,
    monthlyPayment, ltv, termInMonths, totalConsolidationAmount,
    balanceToCustomer, monthlySavings, surplusIncomeResult,
    stressedSurplusIncomeResult, insolvencyRuleValue,
    insolvencyRuleResult,
  } = summary;

  const truthyNetLoanAmount = netLoanAmount >= 0 && netLoanAmount !== null;
  const truthyAddedFeesAmount = addedFeesAmount >= 0 && addedFeesAmount !== null;
  const truthyGrossLoanAmount = grossLoanAmount >= 0 && grossLoanAmount !== null;
  const truthyMonthlyPayment = monthlyPayment >= 0 && monthlyPayment !== null;
  const truthyLtv = ltv >= 0 && ltv !== null;
  const truthyTermInMonths = termInMonths >= 0 && termInMonths !== null;
  const truthyBalanceToCustomer = balanceToCustomer !== null;
  const truthyTotalConsolidationAmount = totalConsolidationAmount >= 0 && totalConsolidationAmount !== null;
  const truthyMonthlySavings = monthlySavings !== null;
  const truthySurplusIncomeResult = surplusIncomeResult !== null;
  const truthyStressedSurplusIncomeResult = stressedSurplusIncomeResult !== null;
  const truthyInsolvencyRuleValue = insolvencyRuleValue >= 0 && insolvencyRuleValue !== null;
  const truthyInsolvencyRuleResult = insolvencyRuleResult === null;

  return (
    <>
      {(isMsalAuth || creditSearchCompleted) && (
      <>
        <SidebarFooterMenuItem onClick={onClickMenuTitle}>
          <p>Summary</p>
          <MenuTitleCollapsibleIcon className={open ? 'mi-chevron-up' : 'mi-chevron-down'} />
        </SidebarFooterMenuItem>

        {open && (
          <>
            <SidebarFooterItem title="Net loan" prefix={truthyNetLoanAmount ? '£ ' : ''} value={truthyNetLoanAmount ? netLoanAmount : '-'} />
            <SidebarFooterItem title="Added fees" prefix={truthyAddedFeesAmount ? '£ ' : ''} value={truthyAddedFeesAmount ? addedFeesAmount : '-'} />
            <SidebarFooterItem title="Gross loan" prefix={truthyGrossLoanAmount ? '£ ' : ''} value={truthyGrossLoanAmount ? grossLoanAmount : '-'} />
            <SidebarFooterItem title="Monthly payment" prefix={truthyMonthlyPayment ? '£ ' : ''} value={truthyMonthlyPayment ? monthlyPayment : '-'} />
            <SidebarFooterItem title="LTV" suffix={truthyLtv ? ' %' : ''} value={truthyLtv ? ltv : '-'} />
            <SidebarFooterItem title="Term" suffix={truthyTermInMonths ? ' mths' : ''} value={truthyTermInMonths ? termInMonths?.toFixed(0) : '-'} />

            {stageOnOrAfter(stage, Stages.Affordability) && (
              <>
                <SidebarFooterItem title="Balance to customer" prefix={truthyBalanceToCustomer ? '£ ' : ''} value={truthyBalanceToCustomer ? balanceToCustomer : '-'} />
                <SidebarFooterItem title="Consolidated" prefix={truthyTotalConsolidationAmount ? '£ ' : ''} value={truthyTotalConsolidationAmount ? totalConsolidationAmount : '-'} />
                <SidebarFooterItem title="Monthly saving" prefix={truthyMonthlySavings ? '£ ' : ''} value={truthyMonthlySavings ? monthlySavings : '-'} />
                <SidebarFooterItem title="Surplus income" prefix={truthySurplusIncomeResult ? '£ ' : ''} value={truthySurplusIncomeResult ? surplusIncomeResult : '-'} />
                <SidebarFooterItem title="Surplus stress income" prefix={truthyStressedSurplusIncomeResult ? '£ ' : ''} value={truthyStressedSurplusIncomeResult ? stressedSurplusIncomeResult : '-'} />
                <SidebarFooterItem
                  title={`${truthyInsolvencyRuleValue ? insolvencyRuleValue : 'ERROR'} GDP rule`}
                  value={`${truthyInsolvencyRuleResult ? '-' : insolvencyRuleResult ? 'Pass' : 'Fail'}`}
                  sidebarFooterItemType={insolvencyRuleResult === true ? 'success' : (insolvencyRuleResult === false ? 'error' : 'none')}
                />
              </>
            )}
          </>
        )}
      </>
      )}
    </>
  );
}
